import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BookOpen, GraduationCap, PenLine, type LucideIcon } from 'lucide-react';
import { useFeatureAccess } from '../../../contexts/FeatureAccessContext';
import {
  buildTamilCompositionNavUrl,
  buildTamilKeeladiNavUrl,
} from '../../student-nav/studentPocNavigation';
import { ROUTES } from '../../../utils/constants';

type QuickAction = {
  id: string;
  label: string;
  route: string;
  icon: LucideIcon;
};

export function TamilQuickActions() {
  const { t } = useTranslation();
  const { isSchoolFeatureEnabled, loading: featureAccessLoading } = useFeatureAccess();

  const actions = useMemo(() => {
    if (featureAccessLoading || !isSchoolFeatureEnabled('ai_tutor')) {
      return [] as QuickAction[];
    }

    return [
      {
        id: 'ai-tutor',
        label: t('dashboard.studentHome.tamilQuickActions.aiTutor', {
          defaultValue: 'AI Tutor',
        }),
        route: ROUTES.STUDENT_TAMIL_TUTOR,
        icon: GraduationCap,
      },
      {
        id: 'reading-comprehension',
        label: t('dashboard.studentHome.tamilQuickActions.reading', {
          defaultValue: 'Reading Comprehension',
        }),
        route: buildTamilKeeladiNavUrl(),
        icon: BookOpen,
      },
      {
        id: 'composition',
        label: t('dashboard.studentHome.tamilQuickActions.composition', {
          defaultValue: 'Composition Writing',
        }),
        route: buildTamilCompositionNavUrl(),
        icon: PenLine,
      },
    ];
  }, [featureAccessLoading, isSchoolFeatureEnabled, t]);

  if (featureAccessLoading || actions.length === 0) return null;

  return (
    <section className="mb-6" aria-labelledby="tamil-quick-actions-heading">
      <h2
        id="tamil-quick-actions-heading"
        className="mb-3 text-sm font-bold uppercase tracking-wide text-slate-600 dark:text-slate-400"
      >
        {t('dashboard.studentHome.tamilQuickActions.title', { defaultValue: 'Tamil learning' })}
      </h2>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {actions.map((action) => {
          const Icon = action.icon;
          return (
            <Link
              key={action.id}
              to={action.route}
              className="flex min-h-[88px] flex-col items-center justify-center gap-2 rounded-2xl border border-emerald-200/70 bg-white/90 px-3 py-4 text-center shadow-sm transition hover:border-emerald-300 hover:bg-emerald-50/80 dark:border-emerald-500/25 dark:bg-gray-900/50 dark:hover:bg-emerald-950/30"
              data-testid={`tamil-quick-action-${action.id}`}
            >
              <Icon className="h-5 w-5 text-emerald-600 dark:text-emerald-300" aria-hidden />
              <span className="text-xs font-semibold leading-snug text-slate-800 dark:text-slate-100">
                {action.label}
              </span>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
